import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { ModalButtonWithForm } from 'components/common';
import {
  Checkbox,
  FormGroup,
  LanguageVariants,
  Select,
  Text
} from 'components/common/form';
import { UserAttributesConditionsManager } from '.';

import type { UserCustomAttribute } from 'shared-types';

interface Props {
  attribute: UserCustomAttribute;
  availableAttributes: {
    [key: string]: UserCustomAttribute;
  };
  optionGroupIndex: number;
  updateData: () => Promise<void>;
}

const UserAttributesOptionsSettings = ({
  attribute,
  availableAttributes,
  optionGroupIndex,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages } = useReduxState();

  const optionGroup = attribute.optionGroups[optionGroupIndex];

  const hasMultipleOptionGroups = attribute.optionGroups?.length > 1;

  const handleSubmit = async (values) => {
    let options = [...optionGroup.options];

    if (values.sort === 'alphabetical') {
      options = options.sort((a, b) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
      );
    }

    const data = {
      optionGroups: [
        ...attribute.optionGroups.slice(0, optionGroupIndex),
        {
          ...optionGroup,
          conditions: values.conditions || [],
          customKeyName: values.customKeyName || '',
          customKeyNameTranslations: values.customKeyNameTranslations || {},
          enableKeys: values.enableKeys,
          enableTranslations: values.enableTranslations,
          options,
          sort: values.sort
        },
        ...attribute.optionGroups.slice(optionGroupIndex + 1)
      ]
    };

    await api.put(`/admin/user-custom-attributes/${attribute.id}`, data);

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.commonChangesSavesSuccessfully,
        type: 'success'
      })
    );
  };

  const initialValues = {
    conditions: optionGroup.conditions,
    customKeyName: optionGroup.customKeyName,
    customKeyNameTranslations: optionGroup.customKeyNameTranslations,
    enableKeys: Boolean(optionGroup.enableKeys),
    enableTranslations: Boolean(optionGroup.enableTranslations),
    sort: optionGroup.sort
  };

  return (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonIcon="cog"
      buttonSize="header"
      buttonText={messages.commonSettings}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalTitle={messages.commonSettings}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {({ values }) => (
        <>
          <FormGroup label={messages.commonSort}>
            <Select
              field="sort"
              options={[
                {
                  label: messages.commonAlphabetic,
                  value: 'alphabetical'
                },
                {
                  label: 'Manual',
                  value: 'manual'
                }
              ]}
              style={{ maxWidth: 160 }}
            />
          </FormGroup>

          <FormGroup label={messages.commonOptions}>
            <div>
              <Checkbox
                field="enableTranslations"
                id="enable-translations"
                label={'Enable translations'}
                stopPropagation={true}
                style={{ display: 'inline-block' }}
              />
            </div>

            <div style={{ marginTop: 5 }}>
              <Checkbox
                field="enableKeys"
                id="enable-keys"
                label={'Enable keys'}
                stopPropagation={true}
                style={{ display: 'inline-block' }}
              />
            </div>
          </FormGroup>

          {values.enableKeys ? (
            <FormGroup label={'Custom key name'} maxWidth="360px">
              <Text field="customKeyName" />

              <LanguageVariants field="customKeyNameTranslations" type="text" />
            </FormGroup>
          ) : null}

          {hasMultipleOptionGroups ? (
            <FormGroup>
              <UserAttributesConditionsManager
                availableAttributes={availableAttributes}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesOptionsSettings;
